import { useEffect, useId, useRef, type ReactNode, type RefObject } from "react";
import { Clock, Lock, Send, Square } from "lucide-react";
import { AnimatePresence, motion, useReducedMotion } from "motion/react";
import { CoachAvatar } from "./CoachAvatar";
import { HIT_AREA_FROM_36_BORDERED } from "./hitArea";
import { AMBER, CREAM, CREAM_DIM, CREAM_SOFT, TERRACOTTA, TERRACOTTA_SOFT } from "./tokens";
import { TOKENS } from '../ui/dashboard-primitives'
import { RECESS } from '../consent/grounds'

/**
 * The coach page's input: mascot, auto-growing textarea (capped, then it
 * scrolls), send/stop button.
 *
 * Coach v3 makes this the *same bar* as `CoachDock` — same 40rem width, same
 * radius, padding, opaque surface, shadow, 36px mascot and 36px send button —
 * so arriving from the dock feels like the bar you typed in simply moved up
 * the page. If one of the two gains a state, the other has to gain it too
 * (their shared focus treatment lives in `index.css`).
 *
 * There is no hairline above it and no keyboard hint below it: the bar is a
 * raised opaque surface, and that elevation is what separates it from the
 * transcript. Adding a rule as well is the no-line-rule violation the design
 * system exists to prevent.
 *
 * **A locked composer** (`lock`, #394): while the user's AI allowance is spent
 * the shell hands this a sentence, and send is locked until it takes the
 * sentence away. The words sit directly above the bar in the same column, a
 * quiet strip on the dark recess (`RECESS`) and no border (the no-line rule), attached
 * to the composer and not to the transcript, so they are still there once the
 * coach's message has scrolled away. The textarea stays editable on purpose: a
 * draft is not lost to a limit, it is simply not sent yet. The strip is the
 * textarea's description (`aria-describedby`), so a screen reader that lands
 * on a field it cannot send from is told why.
 *
 * **A shut composer** (`shut`, #399, CS-D11): while the user has not agreed to AI processing the shell
 * hands this a sentence and the way back to the consent. It is the spent budget's strip with another
 * reason, and one difference: the field itself is shut. A spent budget returns by itself, so a draft
 * waits in an open field; the consent needs the person to act, and a field that would only receive text
 * nobody can send is closed and dimmed. The strip carries a "Read and agree" pill, which is the shell's
 * `action` (this component knows nothing of what it opens), and is the field's description, as the
 * lock's is. **Both strips sit on the dark recess** and not the hairline wash they had: over the page
 * gradient's tan peak the line read 3.2 to 3.6:1 (measured in a browser, on the shut strip; the lock's is
 * the same component, the same wash and the same text), and each is the one line that says why the field
 * will not send. The two stay one strip with two reasons (CS-D11). **Focus is not dropped when the field shuts under the caret** (the server
 * refused a turn, so the consent was withdrawn elsewhere): a disabled field cannot keep it, and the
 * keyboard user who pressed Enter would be left on `<body>` and start over from the top of the page, so
 * it goes to the strip's pill, which is the way back.
 *
 * `inputRef` stays a prop rather than an internally-owned ref: the shell also
 * imperatively focuses this same textarea from two places outside the form
 * (on mount, and after starting a new chat), each via
 * `inputRef.current?.focus()` on a delay.
 */
export function Composer(props: {
  value: string;
  onChange: (v: string) => void;
  onSubmit: () => void;
  onStop: () => void;
  isStreaming: boolean;
  disabled: boolean;
  placeholder: string;
  inputRef: RefObject<HTMLTextAreaElement | null>;
  /** Why send is locked, and the element id that describes the field. Null: not locked. */
  lock?: { id: string; text: string } | null;
  /** Why the whole composer is shut (no AI consent), the element id that describes the field, and the way back. Null: open. */
  shut?: { id: string; text: string; action: ReactNode } | null;
}) {
  const {
    value,
    onChange,
    onSubmit,
    onStop,
    isStreaming,
    disabled,
    placeholder,
    inputRef,
    lock = null,
    shut = null,
  } = props;
  // A shut composer is disabled the way a loading one is, plus its strip.
  const fieldDisabled = disabled || shut !== null;

  const inputId = useId();
  const reduceMotion = useReducedMotion();

  // The field had the caret when it was last touched. A blur that arrives with the field already
  // disabled is the browser dropping focus because the field shut, not the person leaving it.
  const fieldHadFocus = useRef(false);
  const stripRef = useRef<HTMLDivElement>(null);
  const isShut = shut !== null;
  useEffect(() => {
    if (!isShut || !fieldHadFocus.current) return;
    fieldHadFocus.current = false;
    stripRef.current?.querySelector<HTMLElement>("button, a[href]")?.focus();
  }, [isShut]);

  // Auto-grow the composer textarea with its content (capped, then it scrolls).
  //
  // An empty field is one row, full stop — it is never measured. That is not
  // an optimisation: **Chrome counts the placeholder in `scrollHeight`**, so
  // an empty textarea measured before its flex row has a usable width wraps
  // "Message the coach…" over a dozen lines and reports far more than the cap
  // (376px at a 26px-wide textarea, measured). The effect would then pin
  // `height: 200px`, and because it only re-runs on `value` nothing unpins it
  // — widening the window does not (verified at a 518px-wide textarea), only
  // the first keystroke does. That is a 222px-tall composer on arrival at
  // /coach, for as long as the user hasn't typed.
  //
  // `height: auto` is the correct empty state anyway: `rows={1}` already sizes
  // it to exactly one row, so there is nothing for a measurement to add.
  //
  // vitest.config.ts sets `css: false` and jsdom has no layout engine, so
  // `scrollHeight` there is always 0 — this cannot regress under the unit
  // suite unless the measurement is stubbed, which Composer.test.tsx does.
  useEffect(() => {
    const el = inputRef.current;
    if (!el) return;
    el.style.height = "auto";
    if (value === "") return;
    el.style.height = `${Math.min(el.scrollHeight, 200)}px`;
  }, [value, inputRef]);

  return (
    <form
      onSubmit={(e) => {
        e.preventDefault();
        if (isStreaming) {
          onStop();
        } else if (lock === null && shut === null) {
          onSubmit();
        }
      }}
      className="shrink-0 pt-2 pb-6"
    >
      <div className="mx-auto w-full max-w-[40rem]">
        <label htmlFor={inputId} className="sr-only">
          Message to the AI coach
        </label>

        {/* Entering is the system answering, so it is quick and eased out; leaving
            is quicker still and only fades (nothing moves away). Opacity and a
            4px rise are the whole of it: it appears a few times a day at most. */}
        <AnimatePresence initial={false}>
          {lock !== null && (
            <motion.p
              key="lock"
              id={lock.id}
              role="status"
              initial={{ opacity: 0, transform: reduceMotion ? "translateY(0px)" : "translateY(4px)" }}
              animate={{
                opacity: 1,
                transform: "translateY(0px)",
                transition: { duration: 0.2, ease: [0.23, 1, 0.32, 1] },
              }}
              exit={{ opacity: 0, transition: { duration: 0.14, ease: [0.23, 1, 0.32, 1] } }}
              className="mb-2 flex items-center gap-2 rounded-[14px] px-3.5 py-2 text-[13px] leading-snug"
              style={{
                background: RECESS,
                color: CREAM_SOFT,
                fontFamily: "var(--font-body)",
              }}
            >
              <Clock className="h-3.5 w-3.5 shrink-0" style={{ color: CREAM_DIM }} aria-hidden />
              <span className="min-w-0 wrap-break-word">{lock.text}</span>
            </motion.p>
          )}
        </AnimatePresence>

        {shut !== null && (
          <div
            id={shut.id}
            ref={stripRef}
            role="status"
            className="mb-2 flex items-center gap-2.5 rounded-[14px] py-2 pl-3.5 pr-2 text-[13px] leading-snug"
            style={{
              background: RECESS,
              color: CREAM_SOFT,
              fontFamily: "var(--font-body)",
            }}
          >
            <Lock className="h-3.5 w-3.5 shrink-0" style={{ color: CREAM_DIM }} aria-hidden />
            <p className="min-w-0 flex-1 wrap-break-word">{shut.text}</p>
            {shut.action}
          </div>
        )}

        <div
          className="coach-composer flex items-center gap-3 rounded-[20px] px-3 py-2.5"
          style={{
            background: "var(--chrome-surface)",
            border: "1px solid " + TOKENS.creamBorder,
            boxShadow:
              "0 18px 48px -16px rgba(20, 14, 12, 0.6), inset 0 1px 0 " + TOKENS.creamHairline,
          }}
        >
          <CoachAvatar size={36} className={shut !== null ? "opacity-60" : undefined} />

          <textarea
            ref={inputRef}
            id={inputId}
            rows={1}
            value={value}
            onChange={(e) => onChange(e.target.value)}
            onFocus={() => {
              fieldHadFocus.current = true;
            }}
            onBlur={(e) => {
              if (!e.currentTarget.disabled) fieldHadFocus.current = false;
            }}
            onKeyDown={(e) => {
              if (
                e.key === "Enter" &&
                !e.shiftKey &&
                !e.nativeEvent.isComposing &&
                !isStreaming
              ) {
                // Enter is never a newline here. While the composer is locked it
                // sends nothing either: the draft stays exactly as typed.
                e.preventDefault();
                if (lock === null && shut === null) onSubmit();
              }
            }}
            placeholder={placeholder}
            disabled={fieldDisabled}
            aria-describedby={shut?.id ?? lock?.id}
            // 16px on touch: under 16px iOS Safari zooms the page on focus (INPUT_BASE in form-primitives).
            className="coach-scroll min-w-0 flex-1 resize-none bg-transparent px-1 py-2 text-base pointer-fine:text-[15px] leading-normal outline-none disabled:opacity-50"
            style={{
              color: CREAM,
              fontFamily: "var(--font-body)",
              maxHeight: "200px",
            }}
          />

          <button
            type="submit"
            disabled={!isStreaming && (!value.trim() || fieldDisabled || lock !== null)}
            aria-label={isStreaming ? "Stop response" : "Send message"}
            /* Focus shows as an *outline*, not a ring. Tailwind's ring paints
               through box-shadow, and this button sets boxShadow inline (the
               amber glow below) — inline always beats a class for the same
               property, so a ring here would never render at all. Same on
               CoachDock's send button, which is the same control. */
            className={`flex h-9 w-9 shrink-0 items-center justify-center rounded-[10px] transition-all duration-200 focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-(--accent-amber) disabled:cursor-not-allowed disabled:opacity-40 active:scale-[0.95] ${HIT_AREA_FROM_36_BORDERED}`}
            style={{
              background: isStreaming ? TERRACOTTA_SOFT : AMBER,
              color: isStreaming ? TERRACOTTA : "#1c1816",
              border: isStreaming
                ? `1px solid ${TERRACOTTA}`
                // eslint-disable-next-line no-restricted-syntax -- pre-existing color-literal drift, tracked in docs/refactor-suggestions.md Phase 0/2
                : "1px solid rgba(232, 197, 160, 0.8)",
              boxShadow: isStreaming ? "none" : "0 6px 18px -6px " + TOKENS.amberEmphasis,
            }}
          >
            {isStreaming ? (
              <Square className="h-3.5 w-3.5" strokeWidth={0} fill="currentColor" aria-hidden />
            ) : (
              <Send className="h-[15px] w-[15px]" strokeWidth={2.2} aria-hidden />
            )}
          </button>
        </div>
      </div>
    </form>
  );
}
